import { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUsedBikeDetailsUnauthorized } from "../../State/UsedBike/Action";
import { getStatesWithCities } from "../../State/State/Action";
import MainCarousel from "../MainCarousel/MainCarousel";
import { 
  HiHeart, 
  HiArrowLeft, 
  HiCurrencyRupee, 
  HiUser, 
  HiInformationCircle,
  HiEye,
  HiMapPin,
  HiPhone,
  HiCalendar
} from "react-icons/hi2";

// Tab configuration
const tabs = [
  { id: 0, label: "Bike Details", icon: <HiInformationCircle className="w-5 h-5" /> },
  { id: 1, label: "Seller Details", icon: <HiUser className="w-5 h-5" /> }
];

export default function UsedBikeDetailShort() {
  const location = useLocation();
  const navigate = useNavigate();
  const bikeId = new URLSearchParams(location.search).get("bikeId");
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const bike = useSelector((store) => store.usedBikesResponse.bike);
  const { statesWithCities = [] } = useSelector((store) => store.stateResponse || {});

  useEffect(() => {
    if (bikeId) {
      setLoading(true);
      dispatch(getUsedBikeDetailsUnauthorized(bikeId));
      setLoading(false);
    }
    // Fetch states with cities to get state name
    dispatch(getStatesWithCities());
  }, [bikeId, dispatch]);

  // Find state name from city
  const sellerStateName = useMemo(() => {
    if (!bike || !bike.sellerCityId) return null;
    
    // First check if bike has state name directly
    if (bike.sellerState || bike.stateName || bike.sellerStateName) {
      return bike.sellerState || bike.stateName || bike.sellerStateName;
    }
    
    // Otherwise, find state from cityId in statesWithCities
    for (const state of statesWithCities) {
      const city = (state.cities || []).find(c => c.cityId === bike.sellerCityId);
      if (city) {
        return state.stateName;
      }
    }
    
    return null;
  }, [bike, statesWithCities]);

  // Show first digits and mask last 4 digits with asterisks
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";
    // Remove any non-digit characters
    const digits = phoneNumber.replace(/\D/g, "");
    if (digits.length <= 4) return "****";
    // Show first digits and mask last 4 with asterisks
    const visiblePart = digits.slice(0, -4);
    return `${visiblePart}****`;
  };

  const renderTabContent = () => {
    if (!bike) return null;

    switch (activeTab) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Bike Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-primary-100 rounded-lg mr-3">
                      <HiInformationCircle className="w-6 h-6 text-primary-600" />
                    </div>
                    <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                      Basic Information
                    </Typography>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Brand</span>
                      <span className="font-semibold text-gray-900">{bike.brand}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Model</span>
                      <span className="font-semibold text-gray-900">{bike.model}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Color</span>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-gray-300"></div>
                        <span className="font-semibold text-gray-900">{bike.color}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Registration</span>
                      <span className="font-semibold text-gray-900">{bike.registrationNumber}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-green-100 rounded-lg mr-3">
                      <HiCalendar className="w-6 h-6 text-green-600" />
                    </div>
                    <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                      Usage Details
                    </Typography>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Running KM</span>
                      <span className="font-semibold text-gray-900">{bike.runningKms || "NA"}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Purchase Year</span>
                      <span className="font-semibold text-gray-900">{bike.purchaseYear}</span>
                    </div>
                    {bike.modelYear && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Model Year</span>
                        <span className="font-semibold text-gray-900">{bike.modelYear}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Description */}
            <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <HiInformationCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                    Description
                  </Typography>
                </div>
                <Typography variant="body1" className="text-gray-600 leading-relaxed" sx={{ fontSize: "1rem" }}>
                  {bike.description}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
              <CardContent className="p-6">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-purple-100 rounded-lg mr-3">
                    <HiUser className="w-6 h-6 text-purple-600" />
                  </div>
                  <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                    Seller Information
                  </Typography>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center py-3 px-4 bg-gray-50 rounded-xl">
                      <HiUser className="w-5 h-5 text-gray-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium block">Seller Name</span>
                        <span className="font-semibold text-gray-900">{bike.sellerName}</span>
                      </div>
                    </div>
                    <div className="flex items-center py-3 px-4 bg-gray-50 rounded-xl">
                      <HiPhone className="w-5 h-5 text-gray-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium block">Contact</span>
                        <span className="font-semibold text-gray-900">
                          {formatPhoneNumber(bike.maskedMobile || bike.sellerMobile)}
                        </span>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate("/login")}
                          className="text-primary-600 hover:text-primary-700 ml-2"
                        >
                          Login to view
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center py-3 px-4 bg-gray-50 rounded-xl">
                      <HiMapPin className="w-5 h-5 text-gray-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium block">City</span>
                        <span className="font-semibold text-gray-900">{bike.sellerCity}</span>
                      </div>
                    </div>
                    {sellerStateName && (
                      <div className="flex items-center py-3 px-4 bg-gray-50 rounded-xl">
                        <HiMapPin className="w-5 h-5 text-gray-600 mr-3" />
                        <div>
                          <span className="text-gray-600 font-medium block">State</span>
                          <span className="font-semibold text-gray-900">
                            {sellerStateName}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start py-3 px-4 bg-gray-50 rounded-xl">
                      <HiMapPin className="w-5 h-5 text-gray-600 mr-3 mt-1" />
                      <div>
                        <span className="text-gray-600 font-medium block">Address</span>
                        <span className="font-semibold text-gray-900">{bike.sellerAddress}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
          <Typography variant="h6" className="text-gray-600">
            Loading bike details...
          </Typography>
        </motion.div>
      </div>
    );
  }

  if (!bike) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <HiHeart className="w-12 h-12 text-gray-400" />
          </div>
          <Typography variant="h5" className="text-gray-500 mb-3 font-semibold">
            Bike not found
          </Typography>
          <Typography variant="body1" className="text-gray-400 mb-6">
            The bike you're looking for doesn't exist or has been removed.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/used-bike-list-router")}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl px-6 py-3 font-semibold"
            startIcon={<HiArrowLeft className="w-5 h-5" />}
          >
            Back to Used Bikes
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Compact Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-primary-600 to-accent-600 text-white py-2 px-4"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button
            variant="outlined"
            onClick={() => navigate("/used-bike-list-router")}
            className="border-white text-white hover:bg-white hover:text-primary-600 rounded-lg px-3 py-1"
            startIcon={<HiArrowLeft className="w-4 h-4" />}
          >
            Back
          </Button>
          <div className="text-center">
            <Typography variant="h6" className="font-bold" sx={{ fontSize: "1.25rem" }}>
              {bike.brand} {bike.model}
            </Typography>
            <Typography variant="body2" className="opacity-90" sx={{ fontSize: "0.875rem" }}>
              {bike.color} • {bike.runningKms || "NA"} KM
            </Typography>
          </div>
          <Button
            variant="outlined"
            className="border-white text-white hover:bg-white hover:text-primary-600 rounded-lg px-3 py-1"
            startIcon={<HiHeart className="w-4 h-4" />}
          >
            Save
          </Button>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Image Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-4"
        >
          <Card className="bg-white rounded-2xl shadow-xl border-0 overflow-hidden" sx={{ borderRadius: "16px" }}>
            <MainCarousel imageList={bike.images || []} />
          </Card>
        </motion.div>

        {/* Price Card - Label Above Price */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-6"
        >
          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl shadow-lg border-0" sx={{ borderRadius: "12px" }}>
            <CardContent className="px-4 py-1 text-center">
              <Typography variant="body2" className="opacity-80 mb-0.5" sx={{ fontSize: "0.7rem" }}>
                Asking Price
              </Typography>
              <div className="flex items-center justify-center">
                <Typography variant="h5" className="font-bold" sx={{ fontSize: "1.5rem" }}>
                  ₹{bike.price?.toLocaleString()}
          </Typography>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Custom Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-8"
        >
          <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
            <CardContent className="p-6">
              <div className="flex gap-1 mb-6 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent" style={{ scrollbarWidth: 'thin' }}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap text-sm ${
                      activeTab === tab.id
                        ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
              
              <AnimatePresence mode="wait">
                {renderTabContent()}
              </AnimatePresence>
              </CardContent>
            </Card>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mb-8"
        >
          <Card className="bg-yellow-50 border border-yellow-200 rounded-xl" sx={{ borderRadius: "12px" }}>
            <CardContent className="p-4">
              <div className="flex items-start">
                <div className="p-1 bg-yellow-100 rounded-lg mr-3 mt-0.5">
                  <HiInformationCircle className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <Typography variant="h6" className="font-semibold text-yellow-800 mb-1" sx={{ fontSize: "1rem" }}>
                    Important Note
                  </Typography>
                  <Typography variant="body2" className="text-yellow-700" sx={{ fontSize: "0.875rem" }}>
                    Blue bike does not verify the authenticity of any bike. Please check all details/documents before purchase.
                  </Typography>
                </div>
              </div>
              </CardContent>
            </Card>
        </motion.div>
        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button
            variant="contained"
            size="large"
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            startIcon={<HiEye className="w-5 h-5" />}
          >
            Contact Seller
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/used-bike-list-router")}
            className="border-2 border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-white rounded-xl px-8 py-4 font-semibold transition-all duration-300"
            startIcon={<HiArrowLeft className="w-5 h-5" />}
          >
            Back to Listings
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
